/**
 * CertifiedEmailView.tsx
 * Página PÚBLICA para ver y verificar un correo electrónico certificado.
 * Accesible en /correo-certificado/:emailId sin autenticación.
 * Diseño sobrio, formal e institucional (apropiado para despachos y jueces).
 */

import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Shield, Mail, FileText, Download, CheckCircle2, CheckCheck, Clock, Eye, AlertTriangle, Loader2, ExternalLink, Hash, Calendar, Scale, User, Lock } from "lucide-react";

const BACKEND_URL = import.meta.env.VITE_BACKEND_URL || "http://localhost:8001";

interface AttachmentFile {
    id: string;
    file_name: string;
    created_at?: string;
}

interface CertifiedEmail {
    id: string;
    from_email?: string;
    from_name?: string;
    to_email: string;
    to_name?: string;
    subject: string;
    body_html: string;
    body_hash: string;
    attachments: AttachmentFile[];
    status: string;
    sent_at?: string;
    delivered_at?: string;
    read_at?: string;
    is_judicial_notification: boolean;
    oath_statement?: string;
    oath_accepted_at?: string;
    process_number?: string;
    created_at?: string;
}

const statusConfig: Record<string, { label: string; color: string; icon: React.ReactNode; bg: string }> = {
    sent: { label: "Enviado", color: "text-slate-700", icon: <Mail className="h-3.5 w-3.5" />, bg: "bg-slate-100 border-slate-300" },
    delivered: { label: "Entregado", color: "text-slate-800", icon: <CheckCircle2 className="h-3.5 w-3.5" />, bg: "bg-slate-100 border-slate-300" },
    read: { label: "Leído", color: "text-emerald-800", icon: <CheckCheck className="h-3.5 w-3.5 text-emerald-600" />, bg: "bg-emerald-50 border-emerald-300" },
    failed: { label: "Fallido", color: "text-red-700", icon: <AlertTriangle className="h-3.5 w-3.5" />, bg: "bg-red-50 border-red-200" },
    draft: { label: "Borrador", color: "text-slate-600", icon: <Clock className="h-3.5 w-3.5" />, bg: "bg-slate-50 border-slate-200" },
};

function formatDateColombia(isoStr?: string): string {
    if (!isoStr) return "No disponible";
    try {
        const d = new Date(isoStr);
        return d.toLocaleString("es-CO", {
            timeZone: "America/Bogota",
            year: "numeric",
            month: "long",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        });
    } catch {
        return isoStr;
    }
}

export default function CertifiedEmailView() {
    const { emailId } = useParams<{ emailId: string }>();
    const [email, setEmail] = useState<CertifiedEmail | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [downloadingId, setDownloadingId] = useState<string | null>(null);

    useEffect(() => {
        if (!emailId) return;
        setLoading(true);
        fetch(`${BACKEND_URL}/api/v1/legal-emails/public/view/${emailId}`)
            .then(async (res) => {
                if (!res.ok) {
                    const err = await res.json().catch(() => ({ detail: "Error desconocido" }));
                    throw new Error(err.detail || `Error ${res.status}`);
                }
                return res.json();
            })
            .then((data) => {
                setEmail(data);
                setError(null);
            })
            .catch((err) => setError(err.message))
            .finally(() => setLoading(false));
    }, [emailId]);

    const handleDownload = async (fileId: string) => {
        setDownloadingId(fileId);
        try {
            const res = await fetch(`${BACKEND_URL}/api/v1/legal-emails/public/attachment/${fileId}?email_id=${emailId}`);
            if (!res.ok) throw new Error("No se pudo generar el enlace de descarga");
            const data = await res.json();
            
            const a = document.createElement("a");
            a.href = data.download_url;
            a.download = data.file_name || "adjunto";
            a.target = "_blank";
            document.body.appendChild(a);
            a.click();
            a.remove();
        } catch (err: any) {
            alert(err.message || "Error descargando archivo");
        } finally {
            setDownloadingId(null);
        }
    };

    if (loading) {
        return (
            <div style={{ minHeight: "100vh", background: "#f8fafc", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <div style={{ textAlign: "center" }}>
                    <Loader2 className="h-8 w-8 animate-spin" style={{ color: "#0f172a", margin: "0 auto 16px" }} />
                    <p style={{ color: "#64748b", fontSize: 14 }}>Cargando constancia de correo certificado...</p>
                </div>
            </div>
        );
    }

    if (error || !email) {
        return (
            <div style={{ minHeight: "100vh", background: "#f8fafc", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <div style={{ maxWidth: 480, background: "#fff", border: "1px solid #cbd5e1", borderRadius: 12, padding: 40, textAlign: "center", boxShadow: "0 4px 12px rgba(0,0,0,0.05)" }}>
                    <AlertTriangle className="h-10 w-10" style={{ color: "#dc2626", margin: "0 auto 16px" }} />
                    <h2 style={{ fontSize: 18, fontWeight: 700, color: "#0f172a", margin: "0 0 8px" }}>Correo no encontrado</h2>
                    <p style={{ fontSize: 13, color: "#64748b", margin: "0 0 24px" }}>{error || "El enlace de verificación no es válido o el registro no existe."}</p>
                    <Link to="/" style={{ display: "inline-block", background: "#0f172a", color: "#fff", padding: "10px 24px", borderRadius: 8, textDecoration: "none", fontWeight: 600, fontSize: 13 }}>
                        Ir a la plataforma
                    </Link>
                </div>
            </div>
        );
    }

    const st = statusConfig[email.status] || statusConfig.sent;

    return (
        <div style={{ minHeight: "100vh", background: "#f8fafc", padding: "32px 16px", fontFamily: "'Segoe UI', Arial, sans-serif" }}>
            {/* Header Badge */}
            <div style={{ maxWidth: 840, margin: "0 auto 20px", textAlign: "center" }}>
                <div style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "#ffffff", padding: "6px 18px", borderRadius: 20, border: "1px solid #cbd5e1" }}>
                    <Shield style={{ height: 16, width: 16, color: "#0f172a" }} />
                    <span style={{ fontSize: 12, fontWeight: 700, color: "#0f172a" }}>Mi Asistente Legal IA</span>
                    <span style={{ fontSize: 10, fontWeight: 700, color: "#475569", background: "#f1f5f9", padding: "2px 8px", borderRadius: 12, border: "1px solid #e2e8f0" }}>Plataforma Oficial</span>
                </div>
            </div>

            <div style={{ maxWidth: 840, margin: "0 auto" }}>
                {/* Main Formal Card */}
                <div style={{ background: "#ffffff", borderRadius: 10, border: "1px solid #cbd5e1", overflow: "hidden", boxShadow: "0 2px 8px rgba(0,0,0,0.04)" }}>
                    {/* Official Light Header */}
                    <div style={{ background: "#ffffff", padding: "28px 36px", color: "#0f172a", borderBottom: "1px solid #e2e8f0", borderTop: "4px solid #1e3a5f" }}>
                        <p style={{ margin: "0 0 4px", fontSize: 10, color: "#64748b", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.15em" }}>Constancia de Notificación Electrónica</p>
                        <h1 style={{ fontSize: 20, fontWeight: 700, margin: "0 0 4px", letterSpacing: "-0.01em", color: "#0f172a" }}>Correo Electrónico Certificado</h1>
                        <p style={{ fontSize: 11, color: "#64748b", margin: 0 }}>Trazabilidad e Integridad Probatoria · Ley 2213 de 2022 / Ley 527 de 1999</p>
                    </div>

                    {/* Judicial Banner (Light Slate / Emerald Accent) */}
                    {email.is_judicial_notification && (
                        <div style={{ padding: "16px 36px", background: "#f8fafc", borderBottom: "1px solid #e2e8f0", borderLeft: "4px solid #10b981", display: "flex", alignItems: "flex-start", gap: 12 }}>
                            <Scale style={{ height: 18, width: 18, color: "#059669", flexShrink: 0, marginTop: 2 }} />
                            <div>
                                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 2 }}>
                                    <p style={{ margin: 0, fontSize: 11, fontWeight: 700, color: "#0f172a", textTransform: "uppercase", letterSpacing: "0.05em" }}>Notificación Judicial Formal — Ley 2213 de 2022 (Art. 8)</p>
                                    <span style={{ fontSize: 9, fontWeight: 700, color: "#047857", background: "#ecfdf5", border: "1px solid #a7f3d0", padding: "1px 6px", borderRadius: 4 }}>
                                        Efecto Legal
                                    </span>
                                </div>
                                <p style={{ margin: 0, fontSize: 11, color: "#475569", lineHeight: 1.5 }}>Este mensaje constituye notificación personal electrónica. Los términos procesales comenzarán a correr dos (2) días hábiles después de que se constate el acuse de recibo o el acceso por parte del destinatario.</p>
                            </div>
                        </div>
                    )}

                    {/* Remitente y Destinatario (Tabla Formal) */}
                    <div style={{ padding: "28px 36px" }}>
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 24 }}>
                            {/* De */}
                            <div style={{ padding: 16, background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 8 }}>
                                <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
                                    <User style={{ height: 12, width: 12, color: "#475569" }} />
                                    <span style={{ fontSize: 10, fontWeight: 700, color: "#475569", textTransform: "uppercase", letterSpacing: "0.05em" }}>Remitente Autorizado (DE):</span>
                                </div>
                                <p style={{ margin: 0, fontSize: 14, fontWeight: 700, color: "#0f172a" }}>{email.from_name || "Remitente"}</p>
                                {email.from_email && <p style={{ margin: "2px 0 0", fontSize: 11, color: "#64748b" }}>{email.from_email}</p>}
                            </div>
                            {/* Para */}
                            <div style={{ padding: 16, background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 8 }}>
                                <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
                                    <Mail style={{ height: 12, width: 12, color: "#475569" }} />
                                    <span style={{ fontSize: 10, fontWeight: 700, color: "#475569", textTransform: "uppercase", letterSpacing: "0.05em" }}>Correo Certificado Para:</span>
                                </div>
                                <p style={{ margin: 0, fontSize: 14, fontWeight: 700, color: "#0f172a" }}>{email.to_name || email.to_email}</p>
                                <p style={{ margin: "2px 0 0", fontSize: 11, color: "#64748b" }}>{email.to_email}</p>
                            </div>
                        </div>

                        {/* Subject + Date + Status */}
                        <div style={{ marginBottom: 24 }}>
                            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16, marginBottom: 8 }}>
                                <div>
                                    <p style={{ margin: "0 0 2px", fontSize: 10, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.05em" }}>Asunto de la Notificación</p>
                                    <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: "#0f172a" }}>{email.subject}</h2>
                                </div>
                                <div style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "5px 12px", borderRadius: 6, border: "1px solid", fontSize: 11, fontWeight: 700 }} className={`${st.bg} ${st.color}`}>
                                    {st.icon}
                                    {st.label}
                                </div>
                            </div>
                            <div style={{ display: "flex", alignItems: "center", gap: 16, fontSize: 11, color: "#64748b" }}>
                                <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
                                    <Calendar style={{ height: 12, width: 12 }} />
                                    Enviado: {formatDateColombia(email.sent_at)}
                                </span>
                                {email.process_number && (
                                    <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
                                        <FileText style={{ height: 12, width: 12 }} />
                                        Expediente: {email.process_number}
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Separator */}
                    <div style={{ height: 1, background: "#e2e8f0", margin: "0 36px" }} />

                    {/* Body Content */}
                    <div style={{ padding: "24px 36px" }}>
                        <p style={{ fontSize: 10, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.05em", margin: "0 0 12px" }}>Contenido Notificado</p>
                        <div
                            style={{ fontSize: 13, color: "#1e293b", lineHeight: 1.7, padding: 24, background: "#f8fafc", borderRadius: 8, border: "1px solid #e2e8f0" }}
                            dangerouslySetInnerHTML={{ __html: email.body_html }}
                        />
                    </div>

                    {/* Attachments */}
                    {email.attachments.length > 0 && (
                        <div style={{ padding: "0 36px 24px" }}>
                            <p style={{ fontSize: 10, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.05em", margin: "0 0 12px" }}>Archivos Adjuntos Vincualdos ({email.attachments.length})</p>
                            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                                {email.attachments.map((att) => (
                                    <div key={att.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 16px", background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 6 }}>
                                        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                                            <FileText style={{ height: 16, width: 16, color: "#475569" }} />
                                            <div>
                                                <p style={{ margin: 0, fontSize: 12, fontWeight: 600, color: "#0f172a" }}>{att.file_name}</p>
                                                {att.created_at && <p style={{ margin: "2px 0 0", fontSize: 10, color: "#94a3b8" }}>{formatDateColombia(att.created_at)}</p>}
                                            </div>
                                        </div>
                                        <button
                                            onClick={() => handleDownload(att.id)}
                                            disabled={downloadingId === att.id}
                                            style={{
                                                display: "inline-flex", alignItems: "center", gap: 6,
                                                padding: "6px 14px", background: "#1e3a5f", color: "#ffffff",
                                                border: "1px solid #0f172a", borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: "pointer",
                                                opacity: downloadingId === att.id ? 0.6 : 1,
                                            }}
                                        >
                                            {downloadingId === att.id
                                                ? <Loader2 className="h-3 w-3 animate-spin" />
                                                : <Download style={{ height: 12, width: 12 }} />}
                                            Descargar Archivo
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Verification Section */}
                    <div style={{ margin: "0 36px 24px", background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 8, overflow: "hidden" }}>
                        <div style={{ padding: "12px 16px", borderBottom: "1px solid #e2e8f0", display: "flex", alignItems: "center", gap: 8, background: "#f1f5f9" }}>
                            <Lock style={{ height: 13, width: 13, color: "#0f172a" }} />
                            <span style={{ fontSize: 11, fontWeight: 700, color: "#0f172a", textTransform: "uppercase", letterSpacing: "0.05em" }}>Constancia de Trazabilidad e Integridad Legal</span>
                        </div>
                        <div style={{ padding: "12px 16px" }}>
                            <table style={{ width: "100%", fontSize: 11, color: "#475569" }}>
                                <tbody>
                                    <tr>
                                        <td style={{ padding: "4px 0", fontWeight: 700, width: 140 }}>ID de Registro:</td>
                                        <td style={{ padding: "4px 0", fontFamily: "monospace", fontSize: 10, wordBreak: "break-all" }}>{email.id}</td>
                                    </tr>
                                    <tr>
                                        <td style={{ padding: "4px 0", fontWeight: 700 }}>Proceso / Expediente:</td>
                                        <td style={{ padding: "4px 0", fontFamily: "monospace" }}>{email.process_number || "—"}</td>
                                    </tr>
                                    <tr>
                                        <td style={{ padding: "4px 0", fontWeight: 700 }}>Fecha de Envío UTC:</td>
                                        <td style={{ padding: "4px 0", fontFamily: "monospace", fontSize: 10 }}>{email.sent_at || "—"}</td>
                                    </tr>
                                    {email.delivered_at && (
                                        <tr>
                                            <td style={{ padding: "4px 0", fontWeight: 700 }}>Fecha de Entrega:</td>
                                            <td style={{ padding: "4px 0", fontFamily: "monospace", fontSize: 10 }}>{email.delivered_at}</td>
                                        </tr>
                                    )}
                                    {email.read_at && (
                                        <tr>
                                            <td style={{ padding: "4px 0", fontWeight: 700 }}>Acuse de Recibo / Lectura:</td>
                                            <td style={{ padding: "4px 0", fontFamily: "monospace", fontSize: 10 }}>{email.read_at}</td>
                                        </tr>
                                    )}
                                    <tr>
                                        <td style={{ padding: "4px 0", fontWeight: 700 }}>
                                            <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
                                                <Hash style={{ height: 10, width: 10 }} /> Hash SHA-256:
                                            </span>
                                        </td>
                                        <td style={{ padding: "4px 0", fontFamily: "monospace", fontSize: 9, wordBreak: "break-all", color: "#0f172a", fontWeight: 700 }}>{email.body_hash}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                        <div style={{ padding: "8px 16px 12px", borderTop: "1px solid #e2e8f0" }}>
                            <p style={{ margin: 0, fontSize: 10, color: "#64748b" }}>
                                El código Hash SHA-256 garantiza la inmutabilidad e integridad del contenido del mensaje de datos. Cumple con la Ley 527 de 1999 de Colombia.
                            </p>
                        </div>
                    </div>

                    {/* Oath Statement */}
                    {email.oath_statement && (
                        <div style={{ margin: "0 36px 24px", padding: "14px 16px", background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 8 }}>
                            <p style={{ margin: "0 0 4px", fontSize: 10, fontWeight: 700, color: "#0f172a", textTransform: "uppercase", letterSpacing: "0.05em" }}>Declaración Juramentada del Remitente (Art. 8 Ley 2213/2022)</p>
                            <p style={{ margin: 0, fontSize: 11, color: "#334155", lineHeight: 1.5 }}>{email.oath_statement}</p>
                            {email.oath_accepted_at && (
                                <p style={{ margin: "6px 0 0", fontSize: 10, color: "#64748b" }}>Fecha Aceptación Juramento: {formatDateColombia(email.oath_accepted_at)}</p>
                            )}
                        </div>
                    )}

                    {/* Light Formal Footer */}
                    <div style={{ padding: "20px 36px", background: "#f8fafc", borderTop: "1px solid #e2e8f0", textAlign: "center", color: "#64748b" }}>
                        <p style={{ margin: "0 0 4px", fontSize: 10, color: "#475569", lineHeight: 1.5 }}>
                            Constancia expedida por el sistema de correo electrónico certificado de Mi Asistente Legal IA en cumplimiento de la Ley 2213 de 2022.
                        </p>
                        <p style={{ margin: "6px 0 0", fontSize: 10, color: "#64748b", fontWeight: 700 }}>Copyright© {new Date().getFullYear()} Mi Asistente Legal IA</p>
                        <a href="https://miasistentelegalia.com" target="_blank" rel="noopener noreferrer" style={{ display: "inline-flex", alignItems: "center", gap: 4, marginTop: 8, fontSize: 10, color: "#1e3a5f", textDecoration: "none", fontWeight: 600 }}>
                            <ExternalLink style={{ height: 10, width: 10 }} /> miasistentelegalia.com
                        </a>
                    </div>
                </div>
            </div>
        </div>
    );
}
